import { useState } from 'react';
import type { OfferFilter, SortMode, WhenChoice } from '../results/list-view.js';
import { Icon } from '../results/Icon.js';
import { weekendFetchDays } from './weekend.js';

/**
 * Filter chips (UI-REDESIGN.md §5, issue #9). Distance + When drive the query
 * params the API already accepts (radiusMiles ≤ 25, windowDays ≤ 14). Offer,
 * audience, confirmed-food, and "happening now" narrow the returned page on
 * the client. Sort defaults to the server ranking ("Best match") and can
 * switch to soonest or nearest without a refetch. Chips are real
 * <button aria-pressed> toggles, single-select per group (toggles excepted),
 * keyboard-operable with a visible focus ring.
 */

export interface QueryFilters {
  radiusMiles: number;
  windowDays: number;
}

export interface FilterState extends QueryFilters {
  when: WhenChoice;
  offer: OfferFilter;
  hideRestricted: boolean;
  confirmedOnly: boolean;
  happeningNow: boolean;
  sort: SortMode;
}

export const DEFAULT_FILTER_STATE: FilterState = {
  radiusMiles: 5,
  windowDays: 3,
  when: 'couple',
  offer: 'either',
  hideRestricted: false,
  confirmedOnly: false,
  happeningNow: false,
  sort: 'best',
};

/** Days to ask the API for. Weekend asks only far enough to reach Sunday night. */
export function windowDaysFor(when: WhenChoice, now: Date = new Date()): number {
  if (when === 'today') return 1;
  if (when === 'couple') return 3;
  if (when === 'two_weeks') return 14;
  return weekendFetchDays(now);
}

export function whenAnalyticsValue(when: WhenChoice, windowDays: number): string {
  return when === 'weekend' ? 'weekend' : String(windowDays);
}

export interface FilterChange {
  name: string;
  value: string;
}

/**
 * Diff two filter bars into GA4 `filter_change` params. Same-value clicks
 * (pressing the chip that is already on) produce nothing.
 */
export function filterChanges(prev: FilterState, next: FilterState): FilterChange[] {
  const changes: FilterChange[] = [];
  if (prev.radiusMiles !== next.radiusMiles) {
    changes.push({ name: 'distance', value: String(next.radiusMiles) });
  }
  if (prev.when !== next.when) {
    changes.push({ name: 'when', value: whenAnalyticsValue(next.when, next.windowDays) });
  } else if (prev.windowDays !== next.windowDays) {
    changes.push({ name: 'when', value: String(next.windowDays) });
  }
  if (prev.happeningNow !== next.happeningNow) {
    changes.push({ name: 'happening_now', value: next.happeningNow ? 'on' : 'off' });
  }
  if (prev.offer !== next.offer) {
    changes.push({ name: 'offer', value: next.offer });
  }
  if (prev.confirmedOnly !== next.confirmedOnly) {
    changes.push({ name: 'confirmed_food', value: next.confirmedOnly ? 'on' : 'off' });
  }
  if (prev.hideRestricted !== next.hideRestricted) {
    changes.push({
      name: 'audience',
      value: next.hideRestricted ? 'hide_restricted' : 'show_all',
    });
  }
  if (prev.sort !== next.sort) {
    changes.push({ name: 'sort', value: next.sort });
  }
  return changes;
}

const DISTANCE_OPTIONS: Array<{ label: string; miles: number }> = [
  { label: '1 mi', miles: 1 },
  { label: '5 mi', miles: 5 },
  { label: '25 mi', miles: 25 },
];

/** Largest distance chip. A widen action cannot offer anything past this. */
export const MAX_DISTANCE_MILES = DISTANCE_OPTIONS[DISTANCE_OPTIONS.length - 1]!.miles;

/**
 * Next distance chip that can include an event `nearestMiles` away.
 * Returns null when the event is past every preset (the 25 mi cap).
 */
export function nextDistancePreset(nearestMiles: number): number | null {
  for (const option of DISTANCE_OPTIONS) {
    if (option.miles >= nearestMiles) return option.miles;
  }
  return null;
}

const WHEN_OPTIONS: Array<{ label: string; when: WhenChoice }> = [
  { label: 'Today', when: 'today' },
  { label: 'Next couple of days', when: 'couple' },
  { label: 'This weekend', when: 'weekend' },
  { label: '2 weeks', when: 'two_weeks' },
];

/** Offer, confidence, audience, and sort sit behind More. Happening now does not. */
export function moreFiltersActive(value: FilterState): boolean {
  return (
    value.offer !== 'either' || value.confirmedOnly || value.hideRestricted || value.sort !== 'best'
  );
}

const OFFER_OPTIONS: Array<{ label: string; value: OfferFilter; icon?: 'utensils' | 'cup' }> = [
  { label: 'Food', value: 'food', icon: 'utensils' },
  { label: 'Drinks', value: 'drink', icon: 'cup' },
  { label: 'Either', value: 'either' },
];

const SORT_OPTIONS: Array<{ label: string; value: SortMode }> = [
  { label: 'Best match', value: 'best' },
  { label: 'Soonest', value: 'soonest' },
  { label: 'Nearest', value: 'nearest' },
];

export function Filters({
  value,
  onChange,
}: {
  value: FilterState;
  onChange: (next: FilterState) => void;
}): JSX.Element {
  const [more, setMore] = useState(moreFiltersActive(value));
  return (
    <section className="filters" aria-label="filter events">
      <div className="filter-group">
        <p className="filter-group__label" id="lbl-when">
          When
        </p>
        <div className="seg" role="group" aria-labelledby="lbl-when">
          {WHEN_OPTIONS.map((option) => (
            <button
              key={option.when}
              type="button"
              aria-pressed={value.when === option.when}
              onClick={() =>
                onChange({
                  ...value,
                  when: option.when,
                  windowDays: windowDaysFor(option.when),
                })
              }
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="chip-row">
          <button
            type="button"
            className="chip"
            aria-pressed={value.happeningNow}
            onClick={() => onChange({ ...value, happeningNow: !value.happeningNow })}
          >
            <Icon name="zap" size={15} />
            Happening now
          </button>
        </div>
      </div>

      <div className="filter-group filter-group--distance">
        <p className="filter-group__label" id="lbl-dist">
          Distance
        </p>
        <div className="chip-row">
          <div className="chip-row" role="group" aria-labelledby="lbl-dist">
            {DISTANCE_OPTIONS.map((option) => (
              <button
                key={option.miles}
                type="button"
                className="chip"
                aria-pressed={value.radiusMiles === option.miles}
                onClick={() => onChange({ ...value, radiusMiles: option.miles })}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="chip chip--more"
            aria-expanded={more}
            aria-controls="more-filters"
            onClick={() => setMore((open) => !open)}
          >
            {more ? 'Less' : 'More'}
          </button>
        </div>
      </div>

      <div id="more-filters" hidden={!more}>
        <div className="filter-group">
          <p className="filter-group__label" id="lbl-offer">
            What&apos;s on offer
          </p>
          <div className="chip-row" role="group" aria-labelledby="lbl-offer">
            {OFFER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="chip"
                aria-pressed={value.offer === option.value}
                onClick={() => onChange({ ...value, offer: option.value })}
              >
                {option.icon ? <Icon name={option.icon} size={15} /> : null}
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <p className="filter-group__label" id="lbl-confidence">
            Confidence
          </p>
          <div className="chip-row" role="group" aria-labelledby="lbl-confidence">
            <button
              type="button"
              className="chip"
              aria-pressed={value.confirmedOnly}
              onClick={() => onChange({ ...value, confirmedOnly: !value.confirmedOnly })}
            >
              <Icon name="check" size={15} strokeWidth={3} />
              Only show confirmed food
            </button>
          </div>
        </div>

        <div className="filter-group">
          <p className="filter-group__label" id="lbl-audience">
            Audience
          </p>
          <div className="chip-row" role="group" aria-labelledby="lbl-audience">
            <button
              type="button"
              className="chip"
              aria-pressed={value.hideRestricted}
              onClick={() => onChange({ ...value, hideRestricted: !value.hideRestricted })}
            >
              <Icon name="users" size={15} />
              Hide restricted-audience events
            </button>
          </div>
        </div>

        <div className="filter-group">
          <p className="filter-group__label" id="lbl-sort">
            Sort
          </p>
          <div className="chip-row" role="group" aria-labelledby="lbl-sort">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="chip"
                aria-pressed={value.sort === option.value}
                onClick={() => onChange({ ...value, sort: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>
          {value.sort === 'best' ? (
            <p className="sort-help">Closest events with the clearest food signal.</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
